import { DateTime } from "luxon"

export function filterKeys(obj, keysToKeep) {
  return keysToKeep.reduce(
    (result, key) => (key in obj ? { ...result, [key]: obj[key] } : result),
    {}
  )
}

export function compact(origObj) {
  const obj = { ...origObj }
  Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key])
  return obj
}

export function promoteKey(obj, keyToPromote) {
  return Object.keys(obj).reduce(
    (result, key) => ({ ...result, [key]: obj[key][keyToPromote] }),
    {}
  )
}

export function haveIntersection(x, y) {
  return x.some((x_el) => y.includes(x_el))
}

export function isEmptyObject(x) {
  if (x.constructor === Date) return false
  return typeof x === "object" && Object.keys(x).length === 0
}

export function isEmptyString(x) {
  return (
    Object.prototype.toString.call(x) === "[object String]" && x.trim() === ""
  )
}

export function isNonEmptyArray(x) {
  return Array.isArray(x) && x.length !== 0
}

export function isEmptyArray(x) {
  return Array.isArray(x) && x.length === 0
}

/* eslint-disable no-self-compare */
export function isNotAValue(x) {
  return (
    x === null ||
    x === undefined ||
    x !== x ||
    isEmptyString(x) ||
    isEmptyObject(x)
  )
}

export function isAValue(x) {
  return !isNotAValue(x)
}

export function isNotANumber(x) {
  return isNotAValue(x) || isNaN(x)
}

export function isANumber(x) {
  return !isNotANumber(x)
}

export function isObject(obj) {
  return obj !== null && typeof obj === "object"
}

export function isObjectWithProperty(obj, prop) {
  return isObject(obj) && prop in obj
}

export function todayInISODate() {
  return DateTime.now().toISODate()
}

export function isDate(x) {
  if (x === null) return false
  if (typeof x === "object" && x.constructor === Date) return true

  if (isNotAValue(x)) return false

  return DateTime.fromISO(x).isValid
}

export function sortBy(list, fn, desc) {
  const byKeySortFunction = (a, b) => {
    const aFirst = -1,
      bFirst = 1

    const aVal = fn(a)
    const bVal = fn(b)

    if (isNotAValue(aVal) && isAValue(bVal)) return aFirst

    if (isNotAValue(bVal) && isAValue(aVal)) return bFirst
    if (isNotAValue(aVal) && isAValue(bVal)) return aFirst

    return aVal <= bVal ? aFirst : bFirst
  }
  if (desc) return list.sort(byKeySortFunction).reverse()
  return list.sort(byKeySortFunction)
}

export function getIndexKeys(arr) {
  const keys = arr.keys()
  const indexKeys = []

  for (const key of keys) {
    indexKeys.push(key.toString())
  }

  return indexKeys
}

export function getNonIndexKeys(arr) {
  const allKeys = Object.keys(arr)
  const indexKeys = getIndexKeys(arr)

  return allKeys.filter((key) => !indexKeys.includes(key))
}

//TODO: Use isNotAValue instead of === null
export function sortByKey(list, key, desc) {
  const byKeySortFunction = (a, b) => {
    const aFirst = -1,
      bFirst = 1

    if (a === null && b === null) return aFirst

    if (b !== null && a === null) return bFirst
    if (a !== null && b === null) return aFirst

    if (key in a && !(key in b)) return aFirst
    if (key in b && !(key in a)) return bFirst

    if (b[key] !== null && a[key] === null) return bFirst
    if (a[key] !== null && b[key] === null) return aFirst

    return a[key] <= b[key] ? aFirst : bFirst
  }

  const sorted = [...list].sort(byKeySortFunction)
  if (desc) {
    sorted.reverse()
  }

  const nonIndexKeys = getNonIndexKeys(list)

  for (const key of nonIndexKeys) {
    sorted[key] = list[key]
  }

  return sorted
}

export function formatISOStringToLocalDateTime(isoString) {
  return DateTime.fromISO(isoString)
}

export function formatDateToLocalDateTime(date) {
  return DateTime.fromJSDate(date)
}

export function formatToLocalDateTime(x) {
  if (!x) return x
  if (typeof x === "object" && x.constructor === Date) {
    return formatDateToLocalDateTime(x)
  }
  return formatISOStringToLocalDateTime(x)
}

export function daysFromToday(x) {
  const compareDate = formatISOStringToLocalDateTime(x)
  const now = DateTime.now()
  return Math.ceil(compareDate.diff(now, "days").days)
}

export function valueFromPlainObject(plainObject, fields) {
  const isValueLabelPair = (field) =>
    ["Company", "Lookup", "Portfolio", "User"].includes(field.kind)

  function getLabel(field, lookupValue) {
    const { lookup_values } = field
    return lookup_values.find((el) => el.value === lookupValue).label
  }

  return fields.reduce((result, field) => {
    const valueIsArray = Array.isArray(plainObject[field.key])
    const valueArray = valueIsArray
      ? plainObject[field.key]
      : [plainObject[field.key]]

    const resultArray = valueArray.map((rawValueEl) => {
      if (isNotAValue(rawValueEl)) return rawValueEl

      const result = { value: rawValueEl, kind: field.kind }

      if (isValueLabelPair(field)) {
        result["label"] = getLabel(field, rawValueEl)
      }

      return result
    })

    const valueBlob = valueIsArray ? resultArray : resultArray[0]

    return {
      ...result,
      [field.key]: valueBlob,
    }
  }, {})
}

export function plainObjectFromValue(value) {
  return Object.keys(value).reduce((result, fieldKey) => {
    const valueIsArray = Array.isArray(value[fieldKey])
    const valueArray = valueIsArray ? value[fieldKey] : [value[fieldKey]]

    const resultArray = valueArray.map((el) => {
      if (isNotAValue(el)) return undefined
      return el.value
    })

    const plainValue = valueIsArray ? resultArray : resultArray[0]

    return {
      ...result,
      [fieldKey]: plainValue,
    }
  }, {})
}

export function wrapArray(obj) {
  if (Array.isArray(obj)) return obj
  if (isNotAValue(obj)) return []

  return [obj]
}

export function distinctBy(list, key) {
  const distinctFunc = (list, el) =>
    list.find((listEl) => listEl[key] === el[key]) ? list : list.concat(el)

  return list.reduce(distinctFunc, [])
}

export function unique(list) {
  const uniqueFunc = (value, index, self) => self.indexOf(value) === index
  return list.filter(uniqueFunc)
}

export function fileExtName(filename) {
  if (filename && filename.lastIndexOf(".") > -1) {
    return filename.substr(filename.lastIndexOf("."))
  } else {
    return ""
  }
}

export function convertHTMLToPlainText(htmlString) {
  const traverseElementNodes = (element, result) => {
    element.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        result.push(node.textContent)
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        traverseElementNodes(node, result)
      }
    })
  }

  const tempElement = document.createElement("div")
  tempElement.innerHTML = htmlString

  const result = []
  traverseElementNodes(tempElement, result)

  return result.join(" ").trim()
}
